// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Package, Check } from 'lucide-react';
import { toast } from 'sonner';

interface VideoPackage {
  id: string;
  vendor_id: string;
  package_type: string;
  price_cents: number;
  description?: string;
  features?: string[];
  is_active: boolean;
}

interface VideoVendor {
  id: string;
  business_name: string;
  description?: string;
  rating_average?: number;
  review_count?: number;
  user_id: string;
}

interface PackageSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendor: VideoVendor | null;
  onPackageSelected: (pkg: VideoPackage) => void;
}

const packageTypeColors = {
  silver: 'bg-gray-100 text-gray-800 border-gray-300',
  gold: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  platinum: 'bg-purple-100 text-purple-800 border-purple-300',
};

const PackageSelectionModal: React.FC<PackageSelectionModalProps> = ({
  open,
  onOpenChange,
  vendor,
  onPackageSelected,
}) => {
  const [packages, setPackages] = useState<VideoPackage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && vendor) {
      fetchVendorPackages();
    }
  }, [open, vendor]);

  const fetchVendorPackages = async () => {
    if (!vendor) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_packages')
        .select('*')
        .eq('vendor_id', vendor.id)
        .eq('is_active', true)
        .order('price_cents', { ascending: true });

      if (error) throw error;

      setPackages(data || []);
    } catch (error) {
      console.error('Error fetching vendor packages:', error);
      toast.error('Failed to load vendor packages');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  };

  const handleChoosePackage = (pkg: VideoPackage) => {
    onPackageSelected(pkg);
    // Don't close the modal here - let the parent handle the flow
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            Choose Video Package
          </DialogTitle>
          <DialogDescription>
            Select a video package from <strong>{vendor?.business_name}</strong>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading packages...</span>
          </div>
        ) : packages.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {packages.map((pkg) => (
              <Card 
                key={pkg.id} 
                className="relative hover:shadow-lg transition-shadow border-2 hover:border-primary/20"
              >
                <CardHeader className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Badge 
                      variant="outline" 
                      className="text-xs font-semibold bg-blue-100 text-blue-800 border-blue-300"
                    >
                      {pkg.package_type.charAt(0).toUpperCase() + pkg.package_type.slice(1)}
                    </Badge>
                  </div>
                  
                  <CardTitle className="text-xl">{pkg.package_type}</CardTitle>
                  
                  <div className="text-3xl font-bold text-primary">
                    {formatPrice(pkg.price_cents)}
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  <CardDescription className="text-sm leading-relaxed text-center">
                    {pkg.description}
                  </CardDescription>
                  
                  {(pkg.features || []).length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-sm">Features included:</h4>
                      <ul className="space-y-1">
                        {(pkg.features || []).map((feature, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm">
                            <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button 
                    className="w-full mt-6"
                    onClick={() => handleChoosePackage(pkg)}
                  >
                    Choose Package
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Packages Available</h3>
            <p className="text-muted-foreground">
              This vendor hasn't set up any video packages yet. Please try another vendor or contact them directly.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PackageSelectionModal;